import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Paper, Button, Grid, Card, CardContent, 
  CircularProgress, Alert, Tab, Tabs, useTheme, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';


// Map API (snake_case) to frontend (camelCase) Measurement type
function mapApiMeasurementToFrontend(m: any): Measurement {
  // Format date to DD-MM-YYYY
  let formattedDate = m.date;
  
  if (m.date && typeof m.date === 'string') {
    // Check for the specific format 20T00:00:00.000Z-05-2025
    if (m.date.includes('T') && m.date.includes('.') && m.date.includes('Z-')) {
      // Extract the day, month, and year from this specific format
      const parts = m.date.split('-');
      if (parts.length >= 3) {
        const day = parts[0].split('T')[0].padStart(2, '0'); // Get the day part
        const month = parts[1].padStart(2, '0'); // Get the month part
        const year = parts[2]; // Get the year part
        formattedDate = `${day}-${month}-${year}`;
      }
    } 
    // Handle standard YYYY-MM-DD format
    else if (m.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const dateParts = m.date.split('-');
      formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
    }
    // Handle other ISO format dates
    else if (m.date.includes('T')) {
      try {
        const isoDate = new Date(m.date);
        if (!isNaN(isoDate.getTime())) {
          const day = isoDate.getDate().toString().padStart(2, '0');
          const month = (isoDate.getMonth() + 1).toString().padStart(2, '0');
          const year = isoDate.getFullYear();
          formattedDate = `${day}-${month}-${year}`;
        }
      } catch (e) {
        console.error('Error parsing date:', m.date, e);
      }
    }
  }
  
  return {
    id: m.id,
    date: formattedDate,
    time: m.time,
    ecCoirBag: String(m.ec_coir_bag ?? ''),
    moistureVMC: String(m.moisture_vmc ?? ''),
    coirBagTemp: String(m.coir_bag_temp ?? ''),
    airTemp: String(m.air_temp ?? ''),
    sbd: String(m.sbd ?? ''),
    cycles: String(m.cycles ?? ''),
    dripVolume: String(m.drip_volume ?? ''),
    dripEC: String(m.drip_ec ?? ''),
    dripPH: String(m.drip_ph ?? ''),
    drainVolume: String(m.drain_volume ?? ''),
    drainEC: String(m.drain_ec ?? ''),
    drainPH: String(m.drain_ph ?? ''),
    runOff: String(m.run_off_percentage ?? ''),
    farm_id: m.farm_id,
    zone_id: m.zone_id,
    user_id: m.user_id,
    created_at: m.created_at,
  };
}

// Types
interface Measurement {
  id: string;
  date: string;
  time: string;
  ecCoirBag: string;
  moistureVMC: string;
  coirBagTemp: string;
  airTemp: string;
  sbd: string;
  cycles: string;
  dripVolume: string;
  dripEC: string;
  dripPH: string;
  drainVolume: string;
  drainEC: string;
  drainPH: string;
  runOff: string;
  farm_id: string;
  zone_id: string;
  user_id: string;
  created_at: string;
}

interface Farm {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
}

interface Zone {
  id: string;
  name: string;
  farm_id: string;
  created_at: string;
}

interface SummaryStats {
  totalFarms: number;
  totalZones: number;
  totalMeasurements: number;
  avgEcCoirBag: number;
  avgMoistureVMC: number;
  avgCoirBagTemp: number;
  avgAirTemp: number;
  avgRunOff: number;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [recentMeasurements, setRecentMeasurements] = useState<Measurement[]>([]);
  const [stats, setStats] = useState<SummaryStats>({
    totalFarms: 0,
    totalZones: 0,
    totalMeasurements: 0,
    avgEcCoirBag: 0,
    avgMoistureVMC: 0,
    avgCoirBagTemp: 0,
    avgAirTemp: 0,
    avgRunOff: 0
  });
  const [tabValue, setTabValue] = useState(0);
  
  // Date range export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Load data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch farms
        const { data: farmsData, error: farmsError } = await api.farms.getAll();
        if (farmsError) throw new Error('Failed to fetch farms');
        setFarms(farmsData || []);
        
        // Fetch all zones
        let allZones: Zone[] = [];
        for (const farm of farmsData || []) {
          const { data: zonesData } = await api.zones.getAll(farm.id);
          if (zonesData) {
            allZones = [...allZones, ...zonesData];
          }
        }
        setZones(allZones);
        
        // Fetch recent measurements (last 30 days)
        console.log('Fetching measurements for zones:', allZones);
        
        let allMeasurements: Measurement[] = [];
        for (const zone of allZones) {
          try {
            const response = await api.measurements.getAll(zone.id);
            console.log(`Measurements for zone ${zone.id}:`, response);
            
            // Handle both formats: { data } object or direct array
            const measurementsData = response.data || response;
            if (Array.isArray(measurementsData)) {
              // Find the farm that this zone belongs to
              const farm = farmsData?.find((farm: Farm) => farm.id === zone.farm_id);
              
              const mappedMeasurements = measurementsData.map(m => {
                // Map the measurement and ensure farm_id is set
                const measurement = mapApiMeasurementToFrontend(m);
                
                // Make sure farm_id is set correctly
                if (!measurement.farm_id && farm) {
                  measurement.farm_id = farm.id;
                  console.log(`Set farm_id ${farm.id} for measurement in zone ${zone.id}`);
                }
                
                return measurement;
              });
              
              console.log('Mapped measurements with farm IDs:', mappedMeasurements);
              allMeasurements = [...allMeasurements, ...mappedMeasurements];
            }
          } catch (err) {
            console.error(`Error fetching measurements for zone ${zone.id}:`, err);
          }
        }
        
        console.log('Total measurements collected:', allMeasurements.length);
        
        // Sort measurements by date (newest first)
        allMeasurements.sort((a, b) => {
          // Parse dates properly based on DD-MM-YYYY format
          let dateA, dateB;
          
          // Parse date A
          if (a.date && a.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
            const [day, month, year] = a.date.split('-').map(Number);
            dateA = new Date(year, month - 1, day);
            // Add time if available
            if (a.time) {
              const [hours, minutes] = a.time.split(':').map(Number);
              dateA.setHours(hours || 0, minutes || 0);
            }
          } else {
            // Fallback to direct parsing
            dateA = new Date(`${a.date}T${a.time || '00:00'}`);
          }
          
          // Parse date B
          if (b.date && b.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
            const [day, month, year] = b.date.split('-').map(Number);
            dateB = new Date(year, month - 1, day);
            // Add time if available
            if (b.time) {
              const [hours, minutes] = b.time.split(':').map(Number);
              dateB.setHours(hours || 0, minutes || 0);
            }
          } else {
            // Fallback to direct parsing
            dateB = new Date(`${b.date}T${b.time || '00:00'}`);
          }
          
          // Compare the dates
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log('Sorted measurements:', allMeasurements.map(m => m.date));
        
        setMeasurements(allMeasurements);
        setRecentMeasurements(allMeasurements.slice(0, 5));
        
        // Calculate summary statistics
        const avgStats = allMeasurements.length > 0 ? {
          avgEcCoirBag: allMeasurements.reduce((sum, m) => sum + parseFloat(m.ecCoirBag || '0'), 0) / allMeasurements.length,
          avgMoistureVMC: allMeasurements.reduce((sum, m) => sum + parseFloat(m.moistureVMC || '0'), 0) / allMeasurements.length,
          avgCoirBagTemp: allMeasurements.reduce((sum, m) => sum + parseFloat(m.coirBagTemp || '0'), 0) / allMeasurements.length,
          avgAirTemp: allMeasurements.reduce((sum, m) => sum + parseFloat(m.airTemp || '0'), 0) / allMeasurements.length,
          avgRunOff: allMeasurements.reduce((sum, m) => sum + parseFloat(m.runOff || '0'), 0) / allMeasurements.length
        } : {
          avgEcCoirBag: 0,
          avgMoistureVMC: 0,
          avgCoirBagTemp: 0,
          avgAirTemp: 0,
          avgRunOff: 0
        };
        
        // Always set stats, even if there are no measurements
        setStats({
          totalFarms: farmsData?.length || 0,
          totalZones: allZones.length,
          totalMeasurements: allMeasurements.length,
          ...avgStats
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Function to open export dialog
  const handleOpenExportDialog = () => {
    // Initialize with default date range (last 30 days)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setStartDate(start);
    setEndDate(end);
    setExportDialogOpen(true);
  };

  // Function to close export dialog
  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  // Function to export data with date range
  const handleExportData = () => {
    if (!startDate || !endDate) {
      alert('Please select a valid date range');
      return;
    }

    if (measurements.length === 0) {
      alert('No data available to export');
      return;
    }

    try {
      // Filter measurements by date range
      const filteredMeasurements = measurements.filter(m => {
        // Parse the date from DD-MM-YYYY format
        let measurementDate;
        
        // Log for debugging
        console.log('Comparing date:', m.date);
        
        if (m.date && typeof m.date === 'string') {
          // Handle DD-MM-YYYY format
          if (m.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
            const [day, month, year] = m.date.split('-').map(Number);
            // Month is 0-indexed in JavaScript Date
            measurementDate = new Date(year, month - 1, day);
          } 
          // Try standard date parsing as fallback
          else {
            measurementDate = new Date(m.date);
          }
        } else {
          // If date is invalid, skip this measurement
          return false;
        }
        
        // Set hours to 0 for all dates to compare only the date part
        const startDateCopy = new Date(startDate);
        startDateCopy.setHours(0, 0, 0, 0);
        
        const endDateCopy = new Date(endDate);
        endDateCopy.setHours(23, 59, 59, 999);
        
        measurementDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
        
        // Log for debugging
        console.log('Parsed date:', measurementDate);
        console.log('Start date:', startDateCopy);
        console.log('End date:', endDateCopy);
        console.log('Is in range:', measurementDate >= startDateCopy && measurementDate <= endDateCopy);
        
        return measurementDate >= startDateCopy && measurementDate <= endDateCopy;
      });

      if (filteredMeasurements.length === 0) {
        alert('No data available in the selected date range');
        return;
      }

      // Sort measurements by date (newest first)
      const sortedMeasurements = [...filteredMeasurements].sort((a, b) => {
        // Parse dates for comparison
        let dateA, dateB;
        
        // Parse date A
        if (a.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
          // DD-MM-YYYY format
          const [day, month, year] = a.date.split('-').map(Number);
          dateA = new Date(year, month - 1, day);
        } else {
          dateA = new Date(a.date);
        }
        
        // Parse date B
        if (b.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
          // DD-MM-YYYY format
          const [day, month, year] = b.date.split('-').map(Number);
          dateB = new Date(year, month - 1, day);
        } else {
          dateB = new Date(b.date);
        }
        
        // Compare times if dates are the same
        if (dateA.getTime() === dateB.getTime()) {
          return a.time > b.time ? -1 : 1; // Newer time first
        }
        
        // Sort by date (newest first)
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('Sorted measurements for export:', sortedMeasurements);
      
      // Format date as DD-MM-YYYY and find zone names
      const dataToExport = sortedMeasurements.map(m => {
        // Format date to DD-MM-YYYY
        let formattedDate = m.date;
        
        // Handle different date formats
        if (m.date && typeof m.date === 'string') {
          // Already in DD-MM-YYYY format
          if (m.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
            formattedDate = m.date;
          }
          // In YYYY-MM-DD format
          else if (m.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = m.date.split('-');
            formattedDate = `${day}-${month}-${year}`;
          }
          // Try to handle other formats
          else if (m.date.includes('-')) {
            const dateParts = m.date.split('-');
            if (dateParts.length === 3) {
              // Check if first part is a 4-digit year
              if (dateParts[0].length === 4) {
                formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
              }
              // Check if last part is a 4-digit year
              else if (dateParts[2].length === 4) {
                formattedDate = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;
              }
            }
          }
        }
        
        console.log('Original date:', m.date, 'Formatted date for Excel:', formattedDate);

        // Find zone name instead of ID
        const zone = zones.find(z => z.id === m.zone_id);
        const zoneName = zone ? zone.name : m.zone_id;

        // DIRECT FIX: Ensure farm name is never empty
        // First try to find the farm by ID
        let farm = farms.find(f => f.id === m.farm_id);
        
        // If not found, try string comparison
        if (!farm && m.farm_id) {
          farm = farms.find(f => String(f.id) === String(m.farm_id));
        }
        
        // Get the farm name with multiple fallbacks to ensure it's never empty
        let farmName = 'Default Farm'; // Guaranteed fallback
        
        if (farm && farm.name) {
          farmName = farm.name; // Use matched farm name if available
        } else if (farms.length > 0 && farms[0].name) {
          farmName = farms[0].name; // Use first farm name as fallback
        } else if (m.farm_id) {
          farmName = `Farm ${m.farm_id}`; // Use ID-based name as fallback
        }
        
        console.log('Final farm name for export:', farmName);

        // Create the export object with guaranteed values for all fields
        const exportRow = {
          'Date': formattedDate,
          'Time': m.time || '',
          'EC in Coir Bag': m.ecCoirBag || '',
          'Moisture VMC %': m.moistureVMC || '',
          'Coir Bag Temp °C': m.coirBagTemp || '',
          'Air Temp °C': m.airTemp || '',
          'SBD': m.sbd || '',
          'Cycles': m.cycles || '',
          'Drip Volume': m.dripVolume || '',
          'Drip EC': m.dripEC || '',
          'Drip pH': m.dripPH || '',
          'Drain Volume': m.drainVolume || '',
          'Drain EC': m.drainEC || '',
          'Drain pH': m.drainPH || '',
          'Run Off %': m.runOff || '',
          'Farm': farmName, // This is now guaranteed to have a value
          'Zone': zoneName || 'Default Zone',
          'Created At': m.created_at || ''
        };
        
        console.log('Export row with farm name:', exportRow);
        return exportRow;
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // DIRECT FIX: Ensure Farm column is populated
      // Find the Farm column index
      const worksheetRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      let farmColIndex = -1;
      
      // Find the Farm column by checking the header row
      for (let col = worksheetRange.s.c; col <= worksheetRange.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellRef] && worksheet[cellRef].v === 'Farm') {
          farmColIndex = col;
          break;
        }
      }
      
      // If Farm column found, ensure all cells have values
      if (farmColIndex >= 0) {
        console.log('Found Farm column at index:', farmColIndex);
        
        // For each row (skip header), ensure Farm cell has a value
        for (let row = 1; row <= worksheetRange.e.r; row++) {
          const farmCellRef = XLSX.utils.encode_cell({ r: row, c: farmColIndex });
          
          // If cell is empty or undefined, set a default value
          if (!worksheet[farmCellRef] || !worksheet[farmCellRef].v) {
            worksheet[farmCellRef] = { t: 's', v: 'Default Farm' };
            console.log(`Set default farm name for row ${row}`);
          }
        }
      } else {
        console.warn('Farm column not found in worksheet!');
      }
      
      // Apply date formatting to the Date column
      // Get the cell reference for the Date column (should be A1 for header, A2+ for data)
      const dateCol = 'A';
      
      // Skip the header row (row 1) and apply formatting to all data rows
      for (let row = worksheetRange.s.r + 1; row <= worksheetRange.e.r; row++) {
        const cellRef = dateCol + (row + 1); // +1 because XLSX rows are 1-indexed
        if (!worksheet[cellRef]) continue;
        
        // Set the cell format to DD-MM-YYYY
        if (!worksheet['!cols']) worksheet['!cols'] = [];
        if (!worksheet['!cols'][0]) worksheet['!cols'][0] = {};
        worksheet['!cols'][0].width = 12; // Set column width
        
        // Ensure the cell has the s (style) property
        if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
        
        // Apply the format
        worksheet[cellRef].z = 'dd-mm-yyyy';
      }
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Measurements');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      
      // Save file
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
      // Format dates for filename
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Generate filename with date range
      const fileName = `strawberry-data-${startDateStr}_to_${endDateStr}.xlsx`;
      
      saveAs(data, fileName);
      handleCloseExportDialog();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  // Prepare chart data
  // Helper function to parse DD-MM-YYYY dates correctly
  const parseDateString = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    
    // Check if date is in DD-MM-YYYY format
    if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [day, month, year] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Fallback to direct parsing
    return new Date(dateStr);
  };

  const prepareTemperatureData = () => {
    // Get last 7 days of data
    const last7Days = measurements
      .filter((m, index) => index < 30) // Limit to prevent excessive processing
      .reduce((acc: Record<string, any>[], m) => {
        const date = m.date;
        const existing = acc.find(item => item.date === date);
        
        if (existing) {
          existing.airTemp = (parseFloat(existing.airTemp) + parseFloat(m.airTemp || '0')) / 2;
          existing.coirBagTemp = (parseFloat(existing.coirBagTemp) + parseFloat(m.coirBagTemp || '0')) / 2;
          existing.count += 1;
        } else {
          acc.push({
            date,
            airTemp: parseFloat(m.airTemp || '0'),
            coirBagTemp: parseFloat(m.coirBagTemp || '0'),
            count: 1
          });
        }
        
        return acc;
      }, [])
      .sort((a, b) => parseDateString(a.date).getTime() - parseDateString(b.date).getTime())
      .slice(0, 7);
    
    return last7Days;
  };

  const prepareMoistureData = () => {
    // Get last 7 days of data
    const last7Days = measurements
      .filter((m, index) => index < 30) // Limit to prevent excessive processing
      .reduce((acc: Record<string, any>[], m) => {
        const date = m.date;
        const existing = acc.find(item => item.date === date);
        
        if (existing) {
          existing.moistureVMC = (parseFloat(existing.moistureVMC) + parseFloat(m.moistureVMC || '0')) / 2;
          existing.runOff = (parseFloat(existing.runOff) + parseFloat(m.runOff || '0')) / 2;
          existing.count += 1;
        } else {
          acc.push({
            date,
            moistureVMC: parseFloat(m.moistureVMC || '0'),
            runOff: parseFloat(m.runOff || '0'),
            count: 1
          });
        }
        
        return acc;
      }, [])
      .sort((a, b) => parseDateString(a.date).getTime() - parseDateString(b.date).getTime())
      .slice(0, 7);
    
    return last7Days;
  };

  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
      </Container>
    );
  }

  console.log('Rendering dashboard with stats:', {
    stats,
    farms,
    zones,
    measurements,
    user
  });

  // Initialize default stats if any values are NaN
  const displayStats = {
    totalFarms: isNaN(stats.totalFarms) ? 0 : stats.totalFarms,
    totalZones: isNaN(stats.totalZones) ? 0 : stats.totalZones,
    totalMeasurements: isNaN(stats.totalMeasurements) ? 0 : stats.totalMeasurements,
    avgEcCoirBag: isNaN(stats.avgEcCoirBag) ? 0 : stats.avgEcCoirBag,
    avgMoistureVMC: isNaN(stats.avgMoistureVMC) ? 0 : stats.avgMoistureVMC,
    avgCoirBagTemp: isNaN(stats.avgCoirBagTemp) ? 0 : stats.avgCoirBagTemp,
    avgAirTemp: isNaN(stats.avgAirTemp) ? 0 : stats.avgAirTemp,
    avgRunOff: isNaN(stats.avgRunOff) ? 0 : stats.avgRunOff
  };

  console.log('Display stats after NaN check:', displayStats);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Dashboard
        </Typography>
        <Button variant="contained" color="primary" href="/data-entry">
          Add New Entry
        </Button>
      </Box>
      
      {/* Welcome Card */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Welcome back, {user?.email}!
        </Typography>
        <Typography color="text.secondary" paragraph>
          Here's an overview of your strawberry growing conditions.
        </Typography>
        
        {/* Summary Statistics */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'primary.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Farms</Typography>
                <Typography variant="h3">{displayStats.totalFarms}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'secondary.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Zones</Typography>
                <Typography variant="h3">{displayStats.totalZones}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'success.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Measurements</Typography>
                <Typography variant="h3">{displayStats.totalMeasurements}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'info.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Avg. Moisture</Typography>
                <Typography variant="h3">{displayStats.avgMoistureVMC.toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Charts */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>7-Day Trends</Typography>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Temperature" />
          <Tab label="Moisture" />
        </Tabs>
        
        {tabValue === 0 && (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={prepareTemperatureData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'Temperature (°C)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="airTemp" name="Air Temp" stroke={theme.palette.primary.main} activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="coirBagTemp" name="Coir Bag Temp" stroke={theme.palette.secondary.main} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        
        {tabValue === 1 && (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={prepareMoistureData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="moistureVMC" name="Moisture VMC" stroke={theme.palette.info.main} activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="runOff" name="Run Off" stroke={theme.palette.success.main} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
      
      {/* Recent Measurements */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>Recent Measurements</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Time</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>EC Coir</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Moisture</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Air Temp</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Coir Temp</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Run Off</th>
              </tr>
            </thead>
            <tbody>
              {recentMeasurements.length > 0 ? (
                recentMeasurements.map((measurement) => (
                  <tr key={measurement.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.date}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.time}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.ecCoirBag}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.moistureVMC}%</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.airTemp}°C</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.coirBagTemp}°C</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{measurement.runOff}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                    No recent measurements found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleOpenExportDialog}
          >
            Export Data
          </Button>
        </Box>
        
        {/* Date Range Export Dialog */}
        <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog}>
          <DialogTitle>Export Data with Date Range</DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={3} sx={{ mt: 2, minWidth: '300px' }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="dd-MM-yyyy"
                  slotProps={{
                    textField: {
                      helperText: 'DD-MM-YYYY'
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  minDate={startDate || undefined}
                  format="dd-MM-yyyy"
                  slotProps={{
                    textField: {
                      helperText: 'DD-MM-YYYY'
                    }
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseExportDialog}>Cancel</Button>
            <Button onClick={handleExportData} variant="contained" color="primary">
              Export
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default DashboardPage;
